/* eslint-disable indent */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../common/state/store";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { IMedia } from "../../../model/unityObject";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getMediaSearch,
  mediaSelector,
  setMediaSearch,
} from "../../../features/gallery/state/gallerySlice";

import { IsLoading } from "../../../common/components/AppState/IsLoading";
import {
  closeStandardModal,
  openStandardModal,
} from "../../../common/state/slice/modal/modalSlice";

import { DynamicField, DynamicMedia } from "./DynamicField";
import ModalGallery from "./ModalGallery";
import { menuItems } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import { useTranslation } from "react-i18next";
import { setAlert } from "../../alert/state/alertsSlice";
import { createNewScene, getCurrentProject } from "../../projects/state/projectsSlice";
import { Field } from "../util/graphFieldsExtractor";
import { PlusIcon } from "@heroicons/react/outline";
import { FooterButtons } from "../../../common/components/FooterButtons";
import canSaveProject from "../util/canSaveProject";
import logoWixarPlaceholder from "../../../common/util/logoWixarPlaceholder";
import { trackEvent } from "../../../trackEvent";

const CREATE_NEW_SCENE_COMPONENT_ID = "createNewSceneComponentId";
const DEFAULT_SCENE_MEDIA_ID = 38;

export const CreateNewScene = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { register, control, getValues, watch, handleSubmit } = useForm<any>({
    defaultValues: { name: t("general.defaultSceneName") },
  });

  const mediasList = useSelector(mediaSelector);
  const currentProject = useSelector(getCurrentProject);
  const currentMediaSearch = useSelector(getMediaSearch);
  const [currentMediaId, setCurrentMediaId] = useState<number>(DEFAULT_SCENE_MEDIA_ID);
  const [currentModel3dId, setCurrentModel3dId] = useState<number>(-1);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const defaultMediaFallback: IMedia = {
    createdAt: "",
    id: DEFAULT_SCENE_MEDIA_ID,
    name: "Wixar Logo",
    label: "",
    organization: 3 as any,
    s3_url: logoWixarPlaceholder,
    type: "image" as any,
    updatedAt: "",
    filename: "logo_wixar.png",
    upload_status: "completed",
  };

  const nameField: Field = {
    type: "text",
    fieldName: "name",
    currentValue: t("general.defaultSceneName"),
  };

  const watchFields = watch();
  useEffect(() => {
    setIsSubmitButtonDisabled(
      (currentMediaId ?? 0) < 1 || getValues("name") === "" || !getValues("name"),
    );
  }, [watchFields]);

  const onSubmit = () => {
    // ?
  };

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  const createScene = () => {
    if (isCreating || isSubmitButtonDisabled) return;

    trackEvent("scene-create");
    setIsCreating(true);
    const sceneName = getValues("name");

    dispatch(
      createNewScene({
        componentId: CREATE_NEW_SCENE_COMPONENT_ID,
        projectId: currentProject.id,
        mediaId: currentMediaId,
        model3d: currentModel3dId,
        sceneName,
      }),
    )
      .then(() => {
        dispatch(setAlert({ type: "success", msg: t("alerts.sceneCreateSuccess") }));
        if (location.pathname.includes("templates")) {
          navigate(`/templates/${currentProject.id}`);
        } else {
          navigate("../graph");
        }
      })
      .catch(() => {
        dispatch(setAlert({ type: "danger", msg: t("alerts.sceneCreateFail") }));
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const getEnhancedField = () => {
    const foundMedia = mediasList.find((m: IMedia) => Number(m.id) === Number(currentMediaId));
    return {
      type: "media",
      mediaType: "image",
      fieldName: "source_media",
      currentValue: currentMediaId,
      relevantSourceMedia:
        foundMedia ?? (currentMediaId === DEFAULT_SCENE_MEDIA_ID ? defaultMediaFallback : undefined),
    };
  };

  const enhancedField = getEnhancedField();

  const getEnhancedFieldModel3d = () => {
    return {
      type: "media",
      mediaType: "model3d",
      fieldName: "source_model3d",
      currentValue: currentMediaId,
      relevantSourceMedia: mediasList.find(
        (m: IMedia) => Number(m.id) === Number(currentModel3dId),
      ),
    };
  };

  const enhancedFieldModel3d = getEnhancedFieldModel3d();

  const handleOpenGallery = (mediaType: string) => {
    dispatch(
      setMediaSearch({
        mediaType,
      }),
    );
    dispatch(openStandardModal("modal-gallery"));
  };

  const onMediaChange = (selectedMedia: IMedia) => {
    dispatch(closeStandardModal("modal-gallery"));

    if (currentMediaSearch.mediaType === "model3d") {
      setCurrentModel3dId(selectedMedia.id);
    } else {
      setCurrentMediaId(selectedMedia.id);
    }
  };

  const removeEnv3d = () => {
    setCurrentModel3dId(-1);
  };

  const model3dMenuItems: menuItems = [
    { menuItemText: t("general.delete"), icon: "DocumentRemoveIcon", itemId: "deleteEnv3d" },
  ];

  const handleButtonClickModel3d = (mediaId: number, item: any) => {
    if (item.itemId === "deleteEnv3d" && mediaId > 0) {
      removeEnv3d();
    } else {
      handleOpenGallery("model3d");
    }
  };

  const canSave = canSaveProject(currentProject);
  return (
    <>
      <ModalGallery
        nodeId={`newscene-${currentProject.id}`}
        onMediaChange={onMediaChange}
        allowedMediaTypeOverride="image|video"
      />
      <div className="flex flex-col w-full h-full mt-4 mx-auto flex flex-col justify-between">
        <IsLoading showSuccess={true} componentId={CREATE_NEW_SCENE_COMPONENT_ID} />
        <form className="relative w-full max-h-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-3xl mx-auto ">
            <div className="w-full flex flex-col">
              <div>
                <h2 className="flex mb-6 items-center justify-center font-bold h-[15%]">
                  {t("general.create")}
                </h2>
              </div>
              <div className=" max-w-md h-[75%]">
                <DynamicField field={nameField} register={register} />
              </div>
            </div>

            <div className="flex pt-2 w-full">
              <div className="mt-2 w-80  mr-9">
                <h2 className=" text-xl w-full max-w-7xl mx-auto mb-4 mt-4">
                  {t("general.backgroundMedia")}
                </h2>
                <div>
                  <Controller
                    control={control}
                    name={enhancedField.fieldName}
                    render={({ field }) => (
                      <DynamicMedia
                        field={field}
                        fieldType={enhancedField.type}
                        register={register}
                        handleButtonClick={() => {
                          handleOpenGallery("image");
                        }}
                        currentMedia={enhancedField.currentValue as number}
                        media={enhancedField.relevantSourceMedia as IMedia}
                      />
                    )}
                  />
                </div>
              </div>

              {currentModel3dId && currentModel3dId > 0 && (
                <div className="w-80 h-72  mr-9 mt-2">
                  <h2 className="text-xl w-full max-w-7xl mx-auto mb-4 mt-4">
                    {t("general.3dEnv")}
                  </h2>
                  <div>
                    <Controller
                      control={control}
                      name={enhancedFieldModel3d.fieldName}
                      render={({ field }) => (
                        <DynamicMedia
                          field={field}
                          fieldType={enhancedFieldModel3d.type}
                          register={register}
                          handleButtonClick={(mediaId, item) => {
                            handleButtonClickModel3d(mediaId, item);
                          }}
                          currentMedia={enhancedFieldModel3d.currentValue as number}
                          media={enhancedFieldModel3d.relevantSourceMedia as IMedia}
                          customMenuItems={model3dMenuItems}
                        />
                      )}
                    />
                  </div>
                </div>
              )}

              {(!currentModel3dId || currentModel3dId < 1) && (
                <div>
                  <h2 className="text-xl w-full max-w-7xl mx-auto  mt-6">{t("general.3dEnv")}</h2>
                  <div>
                    <button
                      className="btn-alternative-outline mt-2.5 button-enviroment3d text-green-500"
                      onClick={() => handleOpenGallery("model3d")}
                    >
                      {<PlusIcon className="h-6 w-6 ml-36 mb-5" />}
                      {t("general.add3dEnv")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
        <FooterButtons
          cancelText={t("general.cancel")}
          cancelCb={() => {
            if (location.pathname.includes("templates")) {
              navigate(`/templates/${currentProject.id}`);
            } else {
              navigate("../graph");
            }
          }}
          nextDisabled={isSubmitButtonDisabled}
          nextText={t("general.save")}
          nextCb={createScene}
          nextDisabledTooltipText={
            canSave.reason ? t(canSave.reason) : t("tooltip.sceneMustHaveNameAndMedia")
          }
        />
      </div>
    </>
  );
};
